import type { DiagnosticCheckResult } from './types'

/**
 * What the diagnostics panel's footer says about its button.
 *
 * Here rather than in `diagnostics-panel.tsx` because the skeleton renders it
 * for real (P-05 §1), and that import would drag the panel into the skeleton's
 * graph. "About two minutes" because the ask rides down on the collector's
 * next heartbeat and the answer comes back off a later one, 60 seconds apart.
 */
export const DIAGNOSTICS_RUN_EXPLANATION =
	'Runs on the collector at its next check-in — a result can take up to about two minutes. Expand a check for its evidence.'

/**
 * The check names the collector reports (API quickstart §6.3). Matched by name,
 * never by position: the report is stored as the collector wrote it, and an
 * older or newer collector may add, drop or reorder checks.
 */
export const DIAGNOSTIC_CHECK_NAMES = {
	listener: 'listener',
	capture: 'capture',
	reachability: 'reachability',
	testLog: 'test log',
} as const

function statusOf(checks: DiagnosticCheckResult[], name: string) {
	return checks.find((check) => check.name === name)?.status
}

/**
 * Whether the result is the common failure pattern: the port is open (the
 * listener passed) but nothing arrived on it (the capture failed). The
 * collector is doing its part, so the next place to look is the device's
 * syslog settings. Read off those two checks alone — not the verdict, which a
 * failing test log or reachability check would also turn to `fail`.
 */
export function isListeningButSilent(checks: DiagnosticCheckResult[]) {
	return (
		statusOf(checks, DIAGNOSTIC_CHECK_NAMES.listener) === 'pass' &&
		statusOf(checks, DIAGNOSTIC_CHECK_NAMES.capture) === 'fail'
	)
}
